import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDebounce } from 'use-debounce';

import CustomSearchbar from 'components/CustomSearchBar';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import deleteDeviceMemberAccessThunk from 'redux-store/thunks/organizationOverview/deleteDeviceMemberAccessThunk';
import getOrganizationByIdThunk from 'redux-store/thunks/organizations/getOrganizationByIdThunk';

import type { AssignMembersToDevicesModalStageProps } from '..';
import MemberList from './MemberList';

const ManageMembers: FC<AssignMembersToDevicesModalStageProps> = ({
  setState,
  organizationId,
  deviceId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [searchString, setSearchString] = useState('');
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const organizationMembers = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.users ?? [],
  );

  const organizationDevices = useAppSelector(
    s => s.organizations.selectedOrganization.organization?.devices ?? [],
  );

  const membersAssignedToDevice = useMemo(
    () => organizationDevices.find(org => org.id === deviceId)?.users || [],
    [organizationDevices, deviceId],
  );

  const currentAssignedMembers = organizationMembers.filter(member =>
    membersAssignedToDevice.includes(member.id),
  );

  const fuse = useMemo(
    () =>
      new Fuse(currentAssignedMembers, {
        keys: ['email'],
        threshold: 0.4,
      }),
    [currentAssignedMembers],
  );

  const handleSearch = useCallback((): void => {
    if (!searchString) {
      setSearchResults(null);

      return;
    }

    const results = fuse.search(searchString);

    setSearchResults(results.map(result => result.item.email));
  }, [fuse, searchString]);

  const [debouncedSearch] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    }
  }, [debouncedSearch, handleSearch]);

  const handleClearSearch = useCallback((): void => {
    setSearchString('');
    setSearchResults(null);
  }, []);

  const filteredMembers = useMemo(
    () =>
      searchResults
        ? currentAssignedMembers.filter(member =>
            searchResults.includes(member.email),
          )
        : currentAssignedMembers,
    [currentAssignedMembers, searchResults],
  );

  const handleUnAssignMemberFromDevice = useCallback(
    async (memberId: number) => {
      if (!organizationId || !deviceId) return;

      try {
        setLoading(true);

        const result = await dispatch(
          deleteDeviceMemberAccessThunk({
            organizationId: organizationId,
            data: {
              device_assignments: [
                {
                  device_id: deviceId,
                  user_ids: [memberId],
                },
              ],
            },
          }),
        ).unwrap();

        if (result.success) {
          await dispatch(
            getOrganizationByIdThunk({ organizationId: organizationId }),
          ).unwrap();
        } else {
          setError(
            t(
              'common:pages.organization_overview.assign_members_to_device.cannot_unassign',
            ),
          );
        }
      } catch (e) {
        Sentry.captureException(e, {
          tags: { page: 'AssignMembersToDevicesModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [deviceId, dispatch, organizationId, t],
  );

  const openAssignMembersStage = useCallback(() => {
    setState({ members: membersAssignedToDevice, stage: 'members' });
  }, [membersAssignedToDevice, setState]);

  return (
    <Box
      sx={{
        height: '45vh',
        overflow: 'auto',
        paddingBottom: 5,
        width: '100%',
      }}
    >
      <Box mb={4}>
        <Typography
          variant="h2"
          mb={2}
          textAlign="center"
          data-testid="manage-members-title"
        >
          {t(
            'common:pages.organization_overview.assign_members_to_device.manage_members',
          )}
        </Typography>
      </Box>
      <Box mb={4} width="100%">
        <ListItemButton
          sx={{
            backgroundColor: theme.vars.palette.grey[100],
            borderRadius: 2,
          }}
          onClick={openAssignMembersStage}
          data-testid="assign-members-button"
        >
          <ListItemText
            color="primary"
            primaryTypographyProps={{
              style: {
                color: theme.vars.palette.primary.dark,
                fontWeight: 500,
              },
            }}
            primary={t(
              'common:pages.organization_overview.assign_members_to_device.assign_member',
            )}
          />
          <IconButton edge="end" disableRipple>
            <AddCircleOutlineRoundedIcon color="primary" />
          </IconButton>
        </ListItemButton>
      </Box>
      <Box>
        <Box mb={3} width="100%">
          <CustomSearchbar
            search={searchString}
            setSearch={setSearchString}
            hasSearched={searchString.length > 0}
            handleSearch={handleSearch}
            handleClearSearch={handleClearSearch}
            testId="assign-members-to-device-search-bar"
          />
        </Box>
        {filteredMembers.length > 0 ? (
          <Box>
            {filteredMembers.map(item => (
              <MemberList
                key={item.email}
                member={item}
                isLoading={isLoading}
                handleUnAssignMemberFromDevice={handleUnAssignMemberFromDevice}
              />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body1"
            textAlign="center"
            data-testid="no-members-found-text"
          >
            {t(
              'common:pages.organization_overview.assign_members_to_device.no_member_found',
            )}
          </Typography>
        )}
        <ErrorText error={error} />
      </Box>
    </Box>
  );
};

export default ManageMembers;
